import { Component } from 'react';
import { Box } from '@/components/ui/lib/box';
import { Heading } from '@/components/ui/lib/heading';
import { Text } from '@/components/ui/lib/text';
import { Button } from '@/components/ui/lib/button';
import { Link } from '@/components/ui/lib/link';
import { DICTIONARY } from '@/config/dictionary';
import { isDev } from '@/config/env';
import './app-error-boundary.css';
import type {
  AppErrorBoundaryProps,
  AppErrorBoundaryState,
} from '@/components/ui/types/app-error-boundary';

class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    if (isDev) console.error('[app error boundary]', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const devMessage = isDev && this.state.error ? this.state.error.message : null;

    return (
      <Box className="error-boundary-root">
        <Heading as="h1" className="error-boundary-heading">
          {DICTIONARY.errors.pageError}
        </Heading>
        <Text as="p" className="error-boundary-description">
          {DICTIONARY.errors.pageErrorDescription}
        </Text>
        {devMessage && (
          <Box as="pre" className="error-boundary-dev-message">
            {devMessage}
          </Box>
        )}
        <Button
          variant="plain"
          className="error-boundary-retry"
          onClick={() => {
            window.location.reload();
          }}
        >
          {DICTIONARY.errors.retry}
        </Button>
        <Text as="p" className="error-boundary-contact">
          {DICTIONARY.errors.contactPrompt}{' '}
          <Link
            href={`mailto:${DICTIONARY.errors.contactEmail}`}
            className="error-boundary-contact-link"
          >
            {DICTIONARY.errors.contactEmail}
          </Link>
        </Text>
      </Box>
    );
  }
}

export default AppErrorBoundary;
